import React, { useMemo } from 'react';
// @ts-ignore — resolved at build time via Vite aliases
import { usePage, EditableToken, RichTextContent } from '@vibesites/runtime/react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { getPropertiesByState, getStateName, getMarketCitySlugs, getMarketProperties } from './propertyUtils';
import { MarketPage, computeBounds } from './MarketPage';
import { CitySEOContent } from './CitySEOContent';

function parseCityFromAlias(alias: string): { stateSlug: string; citySlug: string } | null {
  const parts = alias?.split('--') ?? [];
  if (parts.length >= 3) return { stateSlug: parts[1], citySlug: parts[2] };
  return null;
}

function parseCityFromPath(path: string): { stateSlug: string; citySlug: string } | null {
  const match = path?.match(/^\/apartments\/([^/]+)\/([^/]+)\/?$/);
  if (match) return { stateSlug: match[1], citySlug: match[2] };
  return null;
}

export function CityMarketPage() {
  const propertiesByState = getPropertiesByState();
  const page = usePage();

  const cityInfo = useMemo(() => {
    const fromAlias = parseCityFromAlias(page?.alias || '');
    const fromPath = parseCityFromPath(page?.path || '');
    const parsed = fromAlias || fromPath;
    if (parsed) return parsed;

    for (const state of Object.values(propertiesByState)) {
      const cities = Object.values(state.cities || {});
      if (cities.length > 0) {
        return { stateSlug: state.slug, citySlug: cities[0].slug };
      }
    }
    return { stateSlug: '', citySlug: '' };
  }, [page, propertiesByState]);

  const stateData = useMemo(() => {
    if (!cityInfo.stateSlug) return null;
    return propertiesByState[cityInfo.stateSlug] || null;
  }, [propertiesByState, cityInfo.stateSlug]);

  const cityData = useMemo(() => {
    if (!stateData?.cities || !cityInfo.citySlug) return null;
    return stateData.cities[cityInfo.citySlug] || null;
  }, [stateData, cityInfo.citySlug]);

  // Resolve market grouping: a city page may include properties from
  // neighbouring cities (e.g. Tulsa includes Broken Arrow, Jenks, Owasso).
  const marketProperties = useMemo(() => {
    if (!cityInfo.stateSlug || !cityInfo.citySlug) return [];
    const slugs = getMarketCitySlugs(cityInfo.stateSlug, cityInfo.citySlug);
    return getMarketProperties(cityInfo.stateSlug, slugs);
  }, [cityInfo]);

  const cityName = cityData?.name || cityInfo.citySlug.replace(/-/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase());
  const stateAbbr = stateData?.abbr || cityInfo.stateSlug.toUpperCase();
  const stateName = getStateName(stateAbbr);

  const initialBounds = useMemo(() => {
    return computeBounds(marketProperties);
  }, [marketProperties]);

  return (
    <MarketPage title={`Apartments in ${cityName}, ${stateAbbr}`} initialBounds={initialBounds} properties={marketProperties}>
      <div className="case-seo-content">
        <RichTextContent path="citymarket.seo_heading" label="SEO Heading" defaultValue={contentSchema.tokens['citymarket.seo_heading'].defaultValue} as="h2" />
        <EditableToken path="citymarket.seo_body" type="richtext" label="SEO Body" as="div" style={{ display: 'contents' }}>
          <EditableToken path="citymarket.seo_cta_text" type="text" label="SEO CTA Heading" as="div" style={{ display: 'contents' }}>
            <EditableToken path="citymarket.seo_cta_body" type="richtext" label="SEO CTA Body" as="div" style={{ display: 'contents' }}>
              <CitySEOContent cityName={cityName} stateAbbr={stateAbbr} stateName={stateName} renderHeading={false} />
            </EditableToken>
          </EditableToken>
        </EditableToken>
      </div>
    </MarketPage>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'CityMarket',
  displayName: 'City Market Page',
  tokens: {
    'citymarket.seo_heading': {
      label: 'SEO Heading',
      type: 'richtext',
      defaultValue: 'Apartments in <span data-type="field-token" data-field="property_address_city"></span>, <span data-type="field-token" data-field="property_address_state"></span>',
    },
    'citymarket.seo_body': {
      label: 'SEO Body',
      type: 'richtext',
      defaultValue: '',
    },
    'citymarket.seo_cta_text': {
      label: 'SEO CTA Heading',
      type: 'text',
      defaultValue: '',
    },
    'citymarket.seo_cta_body': {
      label: 'SEO CTA Body',
      type: 'richtext',
      defaultValue: '',
    },
  },
};

(CityMarketPage as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default CityMarketPage;
